import { orderBy } from 'lodash';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { arrowUpArrowDownSvg, binocularsCharacterPng } from '../assets/images';
import { LiveClassFeedbackModal } from '../components/LiveClassFeedbackModal';
import LiveClassesOrderByDropdown from '../components/LiveClassesOrderByDropdown';
import { SITE_LINKS } from '../constants';
import { Button } from './Button';
import LiveClassListItem, { LiveClassListItemProps } from './LiveClassListItem';

export interface LiveClassListProps {
  liveClasses: LiveClassListItemProps[] | undefined;
}

export const LC_SORTABLE_FIELDS = {
  Name: 'name',
  DateTime: 'dateTime',
};

export const LiveClassList: FC<LiveClassListProps> = (props) => {
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortedLiveClasses, setSortedLiveClasses] = useState(props.liveClasses);

  const __ = useTranslation().t;

  const [activeFeedback, setActiveFeedback] = useState<string | null>(null);

  const sortLiveClasses = (field: string) => {
    if (field === sortField) {
      setSortField(`!${field}`);
    } else {
      setSortField(field);
    }
    setSortedLiveClasses(
      orderBy(props.liveClasses, [field], [field === sortField ? 'desc' : 'asc']),
    );
  };

  const orderByDropdownItems = [
    { title: __('name'), onClick: () => sortLiveClasses(LC_SORTABLE_FIELDS.Name) },
    { title: __('date'), onClick: () => sortLiveClasses(LC_SORTABLE_FIELDS.DateTime) },
  ];

  const ArrowUpArrowDown = () => <img src={arrowUpArrowDownSvg} alt="Arrow Up Arrow Down" />;

  const TitleListItem = () => (
    <div className="oejr-lc-list-item-titles">
      <div className="oejr-lc-name" onClick={() => sortLiveClasses(LC_SORTABLE_FIELDS.Name)}>
        <ArrowUpArrowDown />
        <span>{__('class')}</span>
      </div>
      <div
        className="oejr-lc-date-time"
        onClick={() => sortLiveClasses(LC_SORTABLE_FIELDS.DateTime)}
      >
        <ArrowUpArrowDown />
        <span>{__('date')}</span>
      </div>
      <div className="oejr-lc-report">{__('teacherReport')}</div>
      <div className="oejr-lc-recording">{__('seeLiveSession')}</div>
      <div className="oejr-lc-survey">{__('yourFeedback')}</div>
    </div>
  );

  if (!props.liveClasses?.length) {
    return (
      <EmptyList className="oejr-lc-list">
        <StyledImg src={binocularsCharacterPng} alt="Character" />
        <StyledH4>{__('noClassHistory')}</StyledH4>
        <StyledP>{__('startBookingLiveClasses')}</StyledP>
        <Button
          label={__('bookLiveClass')}
          href={SITE_LINKS.LevelDetailsPage()}
          onClick={() => window.localStorage.setItem('openModalReservation', '1')}
        />
      </EmptyList>
    );
  }

  return (
    <>
      <div className="oejr-lc-list">
        <LiveClassesOrderByDropdown items={orderByDropdownItems} />
        <TitleListItem />
        {sortedLiveClasses?.map((liveClass) => (
          <LiveClassListItem
            {...liveClass}
            key={liveClass.dateTime}
            onFeedbackClick={() => setActiveFeedback(liveClass.feedback)}
          />
        ))}
      </div>
      <LiveClassFeedbackModal
        show={!!activeFeedback}
        onClose={() => setActiveFeedback(null)}
        htmlText={activeFeedback}
      />
    </>
  );
};

const StyledH4 = styled.div`
  font-family: ${({ theme }) => theme.fonts.primary.bold.fontFamily};
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.dark};
  margin-top: 24px;
`;

const StyledP = styled.div`
  font-family: ${({ theme }) => theme.fonts.secondary.regular.fontFamily};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.dark};
  margin-top: 8px;
  margin-bottom: 24px;
`;

const EmptyList = styled.div`
  height: calc(100vh - 250px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledImg = styled.img`
  height: 146px;
`;
